import * as React from 'react';
import { Link } from '@tanstack/react-router';
import { RatingChip } from '@/components/ui/rating-chip';
import { PanelSection, SidePanel } from '@/components/ui/workspace';
import type {
  AreaProductRating,
  ClientArea,
  EvaluatedModule,
  ProductInScope,
} from '@/lib/api/vendor-intelligence';
import { VI_STEP_PATH, plural, sharePercent } from './vi-logic';

/**
 * The side panel of one area matrix cell (T-0276; E24-design §4): the
 * server's rating of one vendor in one client area, how much of the area's
 * weight has no evidence, and the module ratings behind it with their
 * weight within the area. Each module is a link into the Ratings grid,
 * where the rating is confirmed or changed; nothing is edited here.
 */
export function AreaCellPanel({
  engagementId,
  area,
  cell,
  product,
  modules,
  onClose,
}: {
  engagementId: string;
  area: ClientArea;
  cell: AreaProductRating;
  product: ProductInScope;
  /** The evaluated modules, for their names and whole weights. */
  modules: readonly EvaluatedModule[];
  onClose: () => void;
}) {
  const moduleOf = React.useMemo(() => new Map(modules.map((m) => [m.module_id, m])), [modules]);
  return (
    <SidePanel
      aria-label={`Fit of ${product.name} in ${area.name}`}
      identifier={product.name}
      path={`${product.vendor.name} · ${area.name}`}
      onClose={onClose}
    >
      <PanelSection label="Rating by area">
        <div className="flex flex-wrap items-center gap-2">
          <RatingChip rating={cell.rating} state="confirmed" />
          <span className="text-body-sm text-ink-700">
            Rolled up by code from the module ratings below, weighted by the area's requirements.
          </span>
        </div>
        <p className="mb-0 mt-1.5 text-meta text-ink-500">
          Weight {area.weight} · value {cell.value.toFixed(2)} of 1 ·{' '}
          {sharePercent(cell.unknown_share)} of the weight Unknown or not rated
          {area.unmapped > 0
            ? ` · ${plural(area.unmapped, 'requirement')} with no module, out of the weight`
            : ''}
        </p>
      </PanelSection>

      <PanelSection label={`Module ratings behind it (${cell.modules.length})`}>
        {cell.modules.length === 0 ? (
          <p className="m-0 text-body-sm text-ink-500">
            No requirement in this area is mapped to a module.
          </p>
        ) : (
          <ul className="m-0 flex list-none flex-col gap-2.5 p-0" aria-label="Module ratings">
            {cell.modules.map((m) => {
              const mod = moduleOf.get(m.module_id);
              const name = mod?.name ?? m.module_id;
              return (
                <li key={m.module_id} className="flex items-start gap-2">
                  <div className="min-w-0 flex-1">
                    <Link
                      to={VI_STEP_PATH}
                      params={{ engagementId, step: 'ratings' }}
                      search={{ product: product.product_id, module: m.module_id }}
                      title="Open this rating in the Ratings grid"
                      className="text-body-sm font-medium text-ink-900 underline-offset-2 hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent-amber-600"
                    >
                      {name}
                    </Link>
                    <span className="block text-meta text-ink-500">
                      Weight {m.weight_in_area} in this area
                      {mod ? ` · ${mod.weight} in the module` : ''}
                    </span>
                  </div>
                  {m.rating ? (
                    <RatingChip rating={m.rating} state="confirmed" size="sm" />
                  ) : (
                    <span className="text-meta text-ink-300">Not rated</span>
                  )}
                </li>
              );
            })}
          </ul>
        )}
        <p className="mb-0 mt-2.5 text-meta text-ink-500">
          A module opens in the Ratings grid, where its rating is confirmed or changed with a
          reason. A change there changes this cell on the next read.
        </p>
      </PanelSection>
    </SidePanel>
  );
}
